// ─── Teacher Training: data rules shared by the SPA, quiz and static pages ────
// Phase B. A Teacher Training (YTT) is a retreat with retreat_type =
// "teacher_training". Every destination, filter option, count and quick link is
// derived from the programmes passed in — never from a fixed list — so a school
// publishing from any country appears everywhere the moment it goes live.
//
// CommonJS so scripts/build-content.js can require it; pure functions only.

const isYtt = (r) => !!r && r.retreat_type === "teacher_training";

// Flexible / year-round programmes (availability_type "flexible") have no
// start_date: they match any month their window touches, and sort by the first
// day a stay can start. Same rules as matchesMonth / sortDate in
// src/retreatAvailability.js, inlined because this file must stay CommonJS.
const isFlexibleYtt = (r) => !!r && r.availability_type === "flexible";
const monthMatches = (r, ym) => {
  if (!ym) return true;
  if (!isFlexibleYtt(r)) return String(r.start_date || "").slice(0, 7) === ym;
  if (r.available_from && r.available_from > `${ym}-31`) return false;
  if (r.available_until && r.available_until < `${ym}-01`) return false;
  return true;
};
const startKey = (r) => {
  if (!isFlexibleYtt(r)) return String(r.start_date || "9999");
  const today = new Date().toISOString().slice(0, 10);
  return r.available_from && r.available_from > today ? r.available_from : today;
};

/** City shown for a programme: its region, else the first part of its location. */
const cityOf = (r) => String((r && (r.region || String(r.location || "").split(",")[0])) || "").trim();
const countryOf = (r) => String((r && r.country) || "").trim();

/** URL slug for any place name ("Nusa Lembongan" → "nusa-lembongan", "Côte d'Ivoire" → "cote-divoire"). */
const slugify = (s) => String(s || "").toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "")
  .replace(/['’]/g, "").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const today = () => new Date().toISOString().slice(0, 10);

/** Price per person for the next batch: early bird while its deadline is ahead. */
function priceOf(r, now = today()) {
  const eb = Number(r.early_bird_price);
  if (eb > 0 && r.early_bird_deadline && r.early_bird_deadline >= now) return eb;
  return Number(r.price) || 0;
}

const countBy = (arr, key) => {
  const acc = {};
  arr.forEach((x) => { const k = key(x); if (k) acc[k] = (acc[k] || 0) + 1; });
  return Object.entries(acc).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([name, count]) => ({ name, count }));
};

/**
 * Filter options built from the programmes that exist.
 * @returns {{ countries, citiesByCountry, hours, languages, styles, priceUsd: {min,max}|null }}
 */
function yttFacets(ytts, toUsd = (a) => a) {
  const list = ytts.filter(isYtt);
  const citiesByCountry = {};
  list.forEach((r) => {
    const c = countryOf(r), city = cityOf(r);
    if (!c || !city) return;
    (citiesByCountry[c] = citiesByCountry[c] || {})[city] = ((citiesByCountry[c] || {})[city] || 0) + 1;
  });
  Object.keys(citiesByCountry).forEach((c) => {
    citiesByCountry[c] = Object.entries(citiesByCountry[c]).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0])).map(([name, count]) => ({ name, count }));
  });
  const prices = list.map((r) => toUsd(priceOf(r), r.currency || "USD")).filter((p) => p > 0);
  return {
    countries: countBy(list, countryOf),
    citiesByCountry,
    hours: [...new Set(list.map((r) => Number(r.certification_hours)).filter(Boolean))].sort((a, b) => a - b),
    languages: countBy(list, (r) => (r.language || "").trim()).map((x) => x.name),
    styles: [...new Set(list.flatMap((r) => r.style || []))].sort(),
    priceUsd: prices.length ? { min: Math.round(Math.min(...prices)), max: Math.round(Math.max(...prices)) } : null,
  };
}

/**
 * f: { country, city, region (a quiz answer, e.g. "Europe"), hours, month ("YYYY-MM"), priceMinUsd, priceMaxUsd, style, language,
 *      accommodation (true = included), yaOnly (true = school lists a Yoga Alliance registry ID), query }
 */
function filterYtt(ytts, f = {}, toUsd = (a) => a) {
  const terms = String(f.query || "").toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2);
  return ytts.filter(isYtt).filter((r) => {
    if (f.country && countryOf(r) !== f.country) return false;
    if (f.region && !matchesQuizRegion(r, f.region)) return false;
    if (f.city && cityOf(r) !== f.city) return false;
    if (f.hours && Number(r.certification_hours) !== Number(f.hours)) return false;
    if (f.month && !monthMatches(r, f.month)) return false;
    if (f.style && f.style !== "All" && !(r.style || []).includes(f.style)) return false;
    if (f.language && (r.language || "").trim() !== f.language) return false;
    if (f.accommodation && !(r.includes_accommodation || (r.room_ids || []).length)) return false;
    if (f.yaOnly && !(r.schools && r.schools.ya_registry_id)) return false;
    const usd = toUsd(priceOf(r), r.currency || "USD");
    if (f.priceMinUsd != null && f.priceMinUsd !== "" && usd < Number(f.priceMinUsd)) return false;
    if (f.priceMaxUsd != null && f.priceMaxUsd !== "" && usd > Number(f.priceMaxUsd)) return false;
    if (terms.length) {
      const hay = [r.title, r.location, r.region, r.country, ...(r.style || []), r.schools && r.schools.name].filter(Boolean).join(" ").toLowerCase();
      if (!terms.every((w) => hay.includes(w))) return false;
    }
    return true;
  });
}

/** Soonest batch first; undated last. */
const sortByNextStart = (list) => [...list].sort((a, b) => startKey(a).localeCompare(startKey(b)));

/**
 * "from 21 to 30 days" — or "23 days" when every value is the same, so a page
 * never says "from 23 to 23". fmt formats one value; null when there are none.
 */
function rangeText(values, fmt = String) {
  const v = values.filter((x) => x != null && !Number.isNaN(x));
  if (!v.length) return null;
  const lo = Math.min(...v), hi = Math.max(...v);
  return lo === hi ? fmt(lo) : `from ${fmt(lo)} to ${fmt(hi)}`;
}

/** Price range for the FAQ, from real programmes only. Null when there are none priced. */
function costRangeUsd(ytts, toUsd = (a) => a) {
  return yttFacets(ytts, toUsd).priceUsd;
}

// Reference geography for the quiz's existing "Europe" answer — which countries
// are in Europe, not where programmes exist. YTT destinations always come from data.
const EUROPE = new Set(["Albania","Andorra","Austria","Belarus","Belgium","Bosnia & Herzegovina","Bulgaria","Croatia","Cyprus","Czechia","Denmark","Estonia","Finland","France","Germany","Greece","Hungary","Iceland","Ireland","Italy","Kosovo","Latvia","Liechtenstein","Lithuania","Luxembourg","Malta","Moldova","Monaco","Montenegro","Netherlands","North Macedonia","Norway","Poland","Portugal","Romania","San Marino","Serbia","Slovakia","Slovenia","Spain","Sweden","Switzerland","Ukraine","United Kingdom","Vatican City"]);

/**
 * The quiz's region answer → which countries it covers. The quiz's original
 * labels keep working; any country with programmes but no label gets its own.
 */
const QUIZ_REGION_RULES = [
  { label: "India (Rishikesh, Goa, Kerala)", match: (c) => c === "India" },
  { label: "Bali, Indonesia", match: (c) => c === "Indonesia" },
  { label: "Thailand", match: (c) => c === "Thailand" },
  { label: "Europe", match: (c) => EUROPE.has(c) },
];
/** Extra quiz options for countries that have programmes but no existing answer. */
function extraQuizRegions(ytts) {
  return yttFacets(ytts).countries.map((c) => c.name).filter((c) => !QUIZ_REGION_RULES.some((r) => r.match(c)));
}
/** Filter for a quiz region answer (an original label or an extra country name). */
function matchesQuizRegion(r, answer) {
  if (!answer || /anywhere/i.test(answer)) return true;
  const rule = QUIZ_REGION_RULES.find((x) => x.label === answer);
  return rule ? rule.match(countryOf(r)) : countryOf(r) === answer;
}

/**
 * Static SEO pages to generate. A page exists for every place with programmes;
 * `index` is true only with 2+ programmes (otherwise noindex, not in sitemap).
 * Paths: /teacher-training/<country>/, /<country>/<city>/, /<hours>-hour/<country>/, /<hours>-hour/<city>/
 */
function seoPages(ytts) {
  const list = ytts.filter(isYtt);
  const pages = new Map();
  const add = (path, title, place, hours, items) => {
    const p = pages.get(path) || { path, title, place, hours, items: [] };
    items.forEach((r) => { if (!p.items.includes(r)) p.items.push(r); });
    pages.set(path, p);
  };
  list.forEach((r) => {
    const c = countryOf(r), city = cityOf(r), h = Number(r.certification_hours);
    if (!c) return;
    add(`/teacher-training/${slugify(c)}/`, `Yoga Teacher Training in ${c}`, c, null, [r]);
    if (city) add(`/teacher-training/${slugify(c)}/${slugify(city)}/`, `Yoga Teacher Training in ${city}, ${c}`, city, null, [r]);
    if (h) {
      add(`/teacher-training/${h}-hour/${slugify(c)}/`, `${h}-Hour Yoga Teacher Training in ${c}`, c, h, [r]);
      if (city) add(`/teacher-training/${h}-hour/${slugify(city)}/`, `${h}-Hour Yoga Teacher Training in ${city}`, city, h, [r]);
    }
  });
  return [...pages.values()].map((p) => ({ ...p, items: sortByNextStart(p.items), index: p.items.length >= 2 }));
}

module.exports = {
  isYtt, cityOf, countryOf, slugify, priceOf, yttFacets, filterYtt, sortByNextStart, costRangeUsd, rangeText,
  EUROPE, QUIZ_REGION_RULES, extraQuizRegions, matchesQuizRegion, seoPages,
};
